import React, { useEffect, useMemo, useState } from "react";
import Sidebar from "../../Component/Sidebar/Sidebar";
import Swal from 'sweetalert2';
import "./transactiontable.css";
import { FaEdit, FaSearch, FaTrash, FaFileExcel } from "react-icons/fa";
import AddTransactionModal from "./AddTransactionModal";
import EditTransactionModal from "./EditTransactionModal";
import * as XLSX from "xlsx";
const API_BASE_URL = process.env.REACT_APP_EXPRESS_API;
const API_URL = `${API_BASE_URL}/api/transactions`;

export default function Transaction() {
  const [transactions, setTransactions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [entries, setEntries] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeForm, setActiveForm] = useState("nlp");
  const [nlpInput, setNlpInput] = useState("");
  const [parsedInput, setParsedInput] = useState("");
  const [dateFilter, setDateFilter] = useState({ from: "", to: "" });

  const [formData, setFormData] = useState({
    description: "",
    nominal: "",
    category_1: "",
    category_2: "",
  });

  // Fetch transaksi
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(API_URL, { method: "GET", credentials: "include" });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || "Failed to fetch transactions");
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.transactions)
            ? data.transactions
            : Array.isArray(data?.data)
              ? data.data
              : [];
        setTransactions(list);
      } catch (err) {

      }
    })();
  }, []);

  // Tambah transaksi
  const handleAddTransaction = async (trx) => {
    try {
      if (activeForm === "manual") {
        if (!formData.description || !formData.nominal || !formData.category_1 || !formData.category_2) {
          alert("Harap isi semua data transaksi terlebih dahulu.");
          return;
        }
        const res = await fetch(API_URL, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        });
        if (!res.ok) {
          const errMsg = await res.text();
          throw new Error(`Gagal menambah transaksi: ${errMsg}`);
        }
        const newTransaction = await res.json();
        setTransactions((prev) => [...prev, newTransaction]);
        Swal.fire({
          icon: 'success',
          title: 'Transaksi Berhasil Ditambahkan',
          html: `
            <div style="text-align: left; font-size: 16px; line-height: 1.6;">
              <p><strong>Deskripsi:</strong> ${newTransaction.description}</p>
              <p><strong>Nominal:</strong> ${newTransaction.nominal}</p>
              <p><strong>Kategori:</strong> ${newTransaction.category_1}</p>
            </div>
          `,
          confirmButtonText: 'OK',
        });
      } else if (activeForm === "nlp") {
        const res = await fetch(API_URL, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(trx),
        });
        if (!res.ok) {
          const errText = await res.text();
          throw new Error(`Gagal tambah transaksi: ${errText}`);
        }
        const newTransaction = await res.json();
        setTransactions((prev) => [...prev, newTransaction]);
        Swal.fire({
          icon: 'success',
          title: 'Transaksi Teridentifikasi',
          html: `
            <div style="text-align: left; font-size: 16px; line-height: 1.6;">
              <p><strong>Deskripsi:</strong> ${newTransaction.description}</p>
              <p><strong>Nominal:</strong> ${newTransaction.nominal}</p>
              <p><strong>Kategori:</strong> ${newTransaction.category_1}</p>
            </div>
          `,
          confirmButtonText: 'OK',
        });
      }
      setShowAddModal(false);
      setFormData({ description: "", nominal: "", category_1: "", category_2: "" });
      setNlpInput("");
    } catch (err) {
      console.error("Error di handleAddTransaction:", err);
      Swal.fire({
        icon: 'error',
        title: 'Gagal Menambahkan Transaksi',
        text: err.message || 'Terjadi kesalahan. Cek console untuk detail.',
      });
    }
  };

  // Edit / Save
  const handleEdit = (t) => {
    setEditingTransaction(t);
    setFormData({
      description: t.description,
      nominal: t.nominal,
      category_1: t.category_1,
      category_2: t.category_2,
    });
  };

  const handleUpdate = async () => {
    try {
      const res = await fetch(`${API_URL}/${editingTransaction._id}`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });
      if (!res.ok) throw new Error("Gagal update transaksi");
      const updated = await res.json();
      setTransactions((prev) => prev.map((t) => (t._id === updated._id ? updated : t)));
      setEditingTransaction(null);
      alert("Transaksi berhasil diperbarui!");
    } catch (err) {
      console.error(err);
      alert("Gagal memperbarui transaksi.");
    }
  };

  // Delete
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm("Yakin ingin menghapus transaksi ini?");
    if (!confirmDelete) return;
    try {
      const res = await fetch(`${API_URL}/${id}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (!res.ok) {
        const errMsg = await res.text();
        console.error("Server response:", errMsg);
        throw new Error(`Gagal menghapus transaksi: ${res.status} ${res.statusText}`);
      }
      setTransactions((prev) => prev.filter((t) => t._id !== id));
      alert("Transaksi berhasil dihapus!");
    } catch (err) {
      console.error("Error saat menghapus transaksi:", err);
      alert("Terjadi kesalahan saat menghapus transaksi. Lihat console untuk detailnya.");
    }
  };

  // Filter + sort newest
  const filteredData = useMemo(() => {
    const lower = searchTerm.toLowerCase();
    const fromDate = dateFilter.from ? new Date(dateFilter.from) : null;
    const toDate = dateFilter.to ? new Date(dateFilter.to) : null;
    const getDate = (t) => new Date(t?.createdAt || t?.updatedAt || t?.date || t?.tanggal || 0);

    const data = transactions.filter((t) => {
      const matchesSearch =
        (t.description || "").toLowerCase().includes(lower) ||
        (t.category_1 || "").toLowerCase().includes(lower) ||
        (t.category_2 || "").toLowerCase().includes(lower);
      const trxDate = getDate(t);
      const matchesDate = (!fromDate || trxDate >= fromDate) && (!toDate || trxDate <= toDate);
      return matchesSearch && matchesDate;
    });

    return data.sort((a, b) => getDate(b) - getDate(a));
  }, [transactions, searchTerm, dateFilter]);

  // Download Excel
  const handleDownloadExcel = () => {
    if (!filteredData.length) {
      alert("Tidak ada data pada rentang tanggal yang dipilih.");
      return;
    }
    const exportData = filteredData.map((t) => {
      const d = new Date(t.createdAt || t.updatedAt || t.date || t.tanggal);
      return {
        Tanggal: d.toLocaleDateString("id-ID"),
        Waktu: d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit" }),
        Deskripsi: t.description,
        Nominal: t.nominal,
        "Kategori Pengeluaran": t.category_1,
        "Jenis Kebutuhan": t.category_2,
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Transaksi");
    const start = dateFilter.from || "Semua";
    const end = dateFilter.to || "Semua";
    XLSX.writeFile(workbook, `Transaksi_${start}-${end}.xlsx`);
  };

  // Pagination
  const indexOfLast = currentPage * entries;
  const indexOfFirst = indexOfLast - entries;
  const currentData = filteredData.slice(indexOfFirst, indexOfLast);
  const totalPages = Math.ceil(filteredData.length / entries);

  return (
    <div className="transaction-page">
      <Sidebar />
      <div className="transaction-content">
        <div className="breadcrumb-transaksi">
          <span className="breadcrumb-item">Halaman</span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item active">Transaksi</span>
        </div>
        <h2>Transaksi</h2>

        {/* Controls */}
        <div className="table-controls">
          {/* kiri */}
          <div className="entries-control">
            Show{" "}
            <select value={entries} onChange={(e) => setEntries(Number(e.target.value))}>
              {[5, 10, 25, 50].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>{" "}
            entries
            <button className="excel-btn" onClick={handleDownloadExcel} title="Export ke Excel">
              <FaFileExcel className="excel-icon" />
              <span>Excel</span>
            </button>
          </div>

          {/* kanan */}
          <div className="right-controls">
            <div className="filters-right">
              <div className="search-control">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>

              <div className="date-filter">
                <input
                  type="date"
                  value={dateFilter.from}
                  onChange={(e) => { setDateFilter({ ...dateFilter, from: e.target.value }); setCurrentPage(1); }}
                />
                <span>to</span>
                <input
                  type="date"
                  value={dateFilter.to}
                  onChange={(e) => { setDateFilter({ ...dateFilter, to: e.target.value }); setCurrentPage(1); }}
                />
              </div>
            </div>

            <button className="add-btn" onClick={() => setShowAddModal(true)}>
              + Tambah
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Tanggal</th>
                <th>Deskripsi</th>
                <th>Nominal</th>
                <th>Kategori Pengeluaran</th>
                <th>Jenis Kebutuhan</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {currentData.length ? (
                currentData.map((t, i) => {
                  const d = new Date(t.createdAt || t.updatedAt || t.date || t.tanggal);
                  return (
                    <tr key={t._id || i}>
                      <td>{d.toLocaleDateString("id-ID")}</td>
                      <td>{t.description}</td>
                      <td>{t.nominal}</td>
                      <td>{t.category_1}</td>
                      <td>{t.category_2}</td>
                      <td>
                        <button className="edit-btn" onClick={() => handleEdit(t)}><FaEdit /></button>
                        <button className="delete-btn" onClick={() => handleDelete(t._id)}><FaTrash /></button>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="no-data">Tidak ada data ditemukan</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Modals */}
        <AddTransactionModal
          showAddModal={showAddModal}
          setShowAddModal={setShowAddModal}
          activeForm={activeForm}
          setActiveForm={setActiveForm}
          formData={formData}
          setFormData={setFormData}
          nlpInput={nlpInput}
          parsedInput={parsedInput}
          setParsedInput={setParsedInput}
          setNlpInput={setNlpInput}
          handleAddTransaction={handleAddTransaction}
        />
        <EditTransactionModal
          editingTransaction={editingTransaction}
          setEditingTransaction={setEditingTransaction}
          formData={formData}
          setFormData={setFormData}
          handleUpdate={handleUpdate}
        />

        {/* Pagination */}
        <div className="pagination">
          <button disabled={currentPage === 1} onClick={() => setCurrentPage(currentPage - 1)}>Sebelum</button>
          {Array.from({ length: totalPages }, (_, i) => i + 1)
            .slice(currentPage - 1, currentPage + 2)
            .map((page) => (
              <button key={page} className={currentPage === page ? "active" : ""} onClick={() => setCurrentPage(page)}>
                {page}
              </button>
            ))}
          <button
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Selanjutnya
          </button>
        </div>
      </div>
    </div>
  );
}